/**
 * ContractorBasicTemplate — single-page contractor / trade business layout.
 * The FIRST shipped Website Builder template (Evolve Drywall pilot).
 *
 * Layout:
 *   <Nav> · <Hero> · <Services> · <About> · <Gallery> · <CTA banner> · <FAQ> · <ContactForm> · <Footer>
 *   <StickyCTA> (mobile only)
 *
 * Renders SEO + LocalBusiness/Service/FAQ JSON-LD via react-helmet-async.
 */
import { Helmet } from 'react-helmet-async';

import WebsiteNav from '../../components/WebsiteNav';
import WebsiteHero from '../../components/WebsiteHero';
import WebsiteServices from '../../components/WebsiteServices';
import WebsiteAbout from '../../components/WebsiteAbout';
import WebsiteGallery from '../../components/WebsiteGallery';
import WebsiteCTA from '../../components/WebsiteCTA';
import WebsiteFAQ from '../../components/WebsiteFAQ';
import WebsiteContactForm from '../../components/WebsiteContactForm';
import WebsiteFooter from '../../components/WebsiteFooter';
import WebsiteStickyCTA from '../../components/WebsiteStickyCTA';

import { buildSeoSchemas } from './schema';

export default function ContractorBasicTemplate({ config }) {
  const seo = config.seo || {};
  const canonical = seo.canonical || (typeof window !== 'undefined' ? window.location.href : '');
  const schemas = buildSeoSchemas(config);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans pb-16 lg:pb-0" data-testid="ws-template-contractor-basic">
      <Helmet>
        <title>{seo.title || `${config.business_name} — ${config.industry}`}</title>
        {seo.description && <meta name="description" content={seo.description} />}
        {Array.isArray(seo.keywords) && seo.keywords.length > 0 && (
          <meta name="keywords" content={seo.keywords.join(', ')} />
        )}
        {canonical && <link rel="canonical" href={canonical} />}

        {/* Per-customer favicon — overrides the host page's default Agent9 favicon */}
        {config.logo?.favicon_ico && <link rel="icon" type="image/x-icon" href={config.logo.favicon_ico} />}
        {config.logo?.favicon_32 && <link rel="icon" type="image/png" sizes="32x32" href={config.logo.favicon_32} />}
        {config.logo?.favicon_192 && <link rel="icon" type="image/png" sizes="192x192" href={config.logo.favicon_192} />}
        {config.logo?.apple_touch_icon && <link rel="apple-touch-icon" href={config.logo.apple_touch_icon} />}
        {(config.brand?.accent || config.brand?.primary) && (
          <meta name="theme-color" content={config.brand.accent || config.brand.primary} />
        )}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title || config.business_name} />
        {seo.description && <meta property="og:description" content={seo.description} />}
        {canonical && <meta property="og:url" content={canonical} />}
        {(seo.og_image || config.logo?.favicon_512) && (
          <meta property="og:image" content={seo.og_image || config.logo.favicon_512} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title || config.business_name} />
        {seo.description && <meta name="twitter:description" content={seo.description} />}

        {/* JSON-LD: LocalBusiness + Service + FAQPage */}
        {schemas.map((s, i) => (
          <script type="application/ld+json" key={i}>
            {JSON.stringify(s)}
          </script>
        ))}
      </Helmet>

      <WebsiteNav config={config} />
      <main>
        <WebsiteHero config={config} />
        <WebsiteServices config={config} />
        <WebsiteAbout config={config} />
        <WebsiteGallery config={config} />
        <WebsiteCTA config={config} />
        <WebsiteFAQ config={config} />
        <WebsiteContactForm config={config} />
      </main>
      <WebsiteFooter config={config} />
      <WebsiteStickyCTA config={config} />
    </div>
  );
}
